(function () {
  function activeVideo(clip) {
    if (!clip) return null;
    const videos = Array.from(clip.querySelectorAll('video'));
    return videos.find((video) => getComputedStyle(video).display !== 'none') || videos[0] || null;
  }

  async function playClip(clip) {
    if (!clip) return;
    const video = activeVideo(clip);
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');

    try {
      if (video.readyState < 2) video.load();
      await video.play();
      clip.classList.add('is-playing');
    } catch (error) {
      clip.classList.remove('is-playing');
    }
  }

  function pauseClip(clip, resetTime) {
    if (!clip) return;
    clip.classList.remove('is-playing');
    clip.querySelectorAll('video').forEach((video) => {
      video.pause();
      if (resetTime) video.currentTime = 0;
    });
  }

  class L20BoostVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.clips = Array.from(this.querySelectorAll('[data-l20-boost-clip]'));
      this.hotspot = this.querySelector('[data-l20-boost-hotspot]');
      this.mode = this.dataset.mode || 'click';
      this.index = Math.max(
        0,
        this.clips.findIndex((clip) => clip.classList.contains('is-active'))
      );
      if (this.index < 0) this.index = 0;

      if (this.clips.length === 0) return;

      this.handleClick = this.onClick.bind(this);
      this.handleHoldStart = this.onHoldStart.bind(this);
      this.handleHoldEnd = this.onHoldEnd.bind(this);

      if (this.hotspot) {
        if (this.mode === 'hold') {
          this.hotspot.addEventListener('pointerdown', this.handleHoldStart);
          window.addEventListener('pointerup', this.handleHoldEnd);
          window.addEventListener('pointercancel', this.handleHoldEnd);
        } else {
          this.hotspot.addEventListener('click', this.handleClick);
        }
        this.layoutLeader = this.layoutLeader.bind(this);
        this.leaderRo = new ResizeObserver(this.layoutLeader);
        this.leaderRo.observe(this.hotspot);
        this.layoutLeader();
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.layoutLeader);
        }
      }

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        threshold: 0.2
      });
      this.observer.observe(this);
      this.setActive(this.index, true);
    }

    disconnectedCallback() {
      this.dataset.ready = 'false';
      this.hotspot?.removeEventListener('click', this.handleClick);
      this.hotspot?.removeEventListener('pointerdown', this.handleHoldStart);
      window.removeEventListener('pointerup', this.handleHoldEnd);
      window.removeEventListener('pointercancel', this.handleHoldEnd);
      this.leaderRo?.disconnect();
      this.observer?.disconnect();
    }

    onIntersect(entries) {
      const visible = Boolean(entries.find((entry) => entry.isIntersecting));
      if (visible) {
        this.layoutLeader();
        this.classList.add('is-callout-in');
        playClip(this.clips[this.index]);
      } else {
        this.classList.remove('is-callout-in');
        this.clips.forEach((clip) => pauseClip(clip, false));
      }
    }

    onClick(event) {
      event.preventDefault();
      this.setActive((this.index + 1) % this.clips.length);
    }

    onHoldStart(event) {
      event.preventDefault();
      this.classList.add('is-holding');
      this.setActive(Math.min(1, this.clips.length - 1));
    }

    onHoldEnd() {
      if (!this.classList.contains('is-holding')) return;
      this.classList.remove('is-holding');
      this.setActive(0);
    }

    layoutLeader() {
      const hotspot = this.hotspot;
      const svg = this.querySelector('[data-l20-boost-leader]');
      const path = this.querySelector('[data-l20-boost-leader-path]');
      const pulse = hotspot && hotspot.querySelector('.l20-boost-video__pulse');
      const copy = hotspot && hotspot.querySelector('.l20-boost-video__hotspot-copy');
      if (!hotspot || !svg || !path || !pulse || !copy) return;

      const extraRaw = parseFloat(getComputedStyle(hotspot).getPropertyValue('--l20-boost-leader-extend'));
      const extra = Number.isNaN(extraRaw) ? 8 : extraRaw;
      const isLeft = this.classList.contains('l20-boost-video--label-left');
      const isMobile = window.matchMedia('(max-width: 768.98px)').matches;
      if (isMobile) {
        const label = copy.querySelector('.l20-boost-video__hotspot-label');
        copy.style.width = 'auto';
        copy.style.maxWidth = 'none';
        copy.style.minWidth = '0';
        if (label) label.style.whiteSpace = 'nowrap';
        const full = Math.ceil(copy.scrollWidth || copy.offsetWidth);
        if (label) label.style.whiteSpace = '';
        const twoLine = Math.max(148, Math.ceil(full * 0.58));
        copy.style.minWidth = twoLine + 'px';
        copy.style.width = twoLine + 'px';
        copy.style.maxWidth = twoLine + 'px';
      } else {
        copy.style.minWidth = '';
        copy.style.width = '';
        copy.style.maxWidth = '';
      }
      const yStart = pulse.offsetTop + pulse.offsetHeight * 0.42;
      const yLine = copy.offsetTop + copy.offsetHeight;
      let xStart;
      let xCorner;
      let xEnd;

      if (isLeft) {
        xStart = pulse.offsetLeft + pulse.offsetWidth * 0.28;
        xCorner = copy.offsetLeft + copy.offsetWidth - 2;
        xEnd = copy.offsetLeft - extra;
      } else {
        xStart = pulse.offsetLeft + pulse.offsetWidth * 0.72;
        xCorner = copy.offsetLeft + 2;
        xEnd = copy.offsetLeft + copy.offsetWidth + extra;
      }

      const minX = Math.min(0, xStart, xCorner, xEnd, copy.offsetLeft);
      const minY = Math.min(0, yStart, yLine, copy.offsetTop, pulse.offsetTop);
      const maxX = Math.max(hotspot.offsetWidth, xStart, xCorner, xEnd, copy.offsetLeft + copy.offsetWidth);
      const maxY = Math.max(hotspot.offsetHeight, yStart, yLine, copy.offsetTop + copy.offsetHeight, pulse.offsetTop + pulse.offsetHeight);
      const width = Math.max(1, maxX - minX + 2);
      const height = Math.max(1, maxY - minY + 2);
      const d = 'M ' + (xStart - minX).toFixed(2) + ' ' + (yStart - minY).toFixed(2) + ' L ' + (xCorner - minX).toFixed(2) + ' ' + (yLine - minY).toFixed(2) + ' H ' + (xEnd - minX).toFixed(2);

      svg.style.left = minX + 'px';
      svg.style.top = minY + 'px';
      svg.setAttribute('viewBox', '0 0 ' + width + ' ' + height);
      svg.setAttribute('width', String(width));
      svg.setAttribute('height', String(height));
      path.setAttribute('d', d);

      let len = 1;
      try {
        len = Math.max(1, path.getTotalLength());
      } catch (error) {}
      path.style.setProperty('--leader-len', String(len));
    }

    setActive(index, initial) {
      if (index < 0 || index >= this.clips.length) return;
      if (!initial && index === this.index) {
        playClip(this.clips[index]);
        return;
      }

      this.index = index;
      this.clips.forEach((clip, clipIndex) => {
        const selected = clipIndex === index;
        clip.classList.toggle('is-active', selected);
        if (selected) {
          playClip(clip);
        } else {
          pauseClip(clip, this.mode === 'hold');
        }
      });
    }
  }

  window.initL20BoostVideo = function (root) {
    if (!root || root.tagName.toLowerCase() !== 'l20-boost-video') return;
    if (typeof root.connectedCallback === 'function' && root.dataset.ready !== 'true') {
      root.connectedCallback();
    }
  };

  if (!customElements.get('l20-boost-video')) {
    customElements.define('l20-boost-video', L20BoostVideo);
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target.querySelector('l20-boost-video');
    window.initL20BoostVideo(root);
  });
})();
